import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './csv-upload-card.module.scss'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'

/**
 * Generic CSV upload control, shared by exclusions and evaluations - same shape (pick a file,
 * upload, replace whatever was saved before, show row-level errors on rejection).
 */
const CsvUploadCard = ({
  title,
  description,
  onUpload,
  isUploading,
  currentCount,
  isLoadingCurrent
}) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [rowErrors, setRowErrors] = useState(null)
  const [generalError, setGeneralError] = useState(null)

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0] || null)
    setRowErrors(null)
    setGeneralError(null)
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    setRowErrors(null)
    setGeneralError(null)
    try {
      await onUpload(selectedFile)
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (error) {
      const responseData = error?.response?.data
      if (responseData?.errors?.length) {
        setRowErrors(responseData.errors)
      } else {
        setGeneralError(responseData?.message || t('uploadGenericError'))
      }
    }
  }

  return (
    <div className={styles.container}>
      <p className={styles.title}>{title}</p>
      <p className={styles.description}>{description}</p>
      <p className={styles.status_text}>
        {isLoadingCurrent ? t('loadingStatus') : t('csvCurrentCount', { count: currentCount })}
      </p>
      <div className={styles.controls_wrapper}>
        <input
          ref={fileInputRef}
          type='file'
          accept='.csv'
          onChange={handleFileChange}
          disabled={isUploading}
        />
        <ButtonComponent
          buttonText={t('uploadButton')}
          colorType='secondary'
          size='small'
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
        />
      </div>
      {generalError && <p className={styles.error_list}>{generalError}</p>}
      {rowErrors && (
        <ul className={styles.error_list}>
          {rowErrors.map((rowError, index) => (
            <li key={`row-error-${index}`}>
              {t('rowError', { row: rowError.row, error: rowError.error })}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CsvUploadCard
